// /map/embed.js: Glassing Topo for Glassing Data's pages (glassing.app/data, #592).
//
// The ground is the share page's inset (`inset.ts`), so Glassing Data draws the
// map page's own Topo through the map page's own code: one map on the site
// (Kyle, 2026-10-08). Glassing Data is dark only (bfp/docs/design.md), so the
// inset draws the night map whatever the device's look.
//
// The live closures and fire layer draws on it too, as on /map (Kyle, 2026-10-08,
// option (a)): the same pointer, archive and look G at the night ground. `live`
// resolves once a copy is on the map, or to null when none can be read, and
// answers what a tap on it says (`live-facts.ts`) and what the legend gives.
//
// A Glassing Data page loads this with `import("/map/embed.js")` and draws its
// restrictions, incident points and panels itself
// (bfp/public/scripts/fire-restrictions.js) over the map `mount` hands back. Same
// origin as the page: the style's contract, coverage index, glyphs, sprite and the
// relief's worker are all read from the page's own origin, which is glassing.app.
//
// WENT HIKING'S MAPS TOO (Kyle, 2026-10-10): wenthiking.com imports this same
// file from glassing.app and draws its pins over the day map with no live
// layer (`night: false, live: false`). A page on another site is why `mount`
// states where it was loaded from (`setSiteOrigin`) before the style is built,
// and why /assets answers with CORS (`public/_headers`).
//
// The built file has a fixed name (vite.config.ts) because a page outside this
// build names it; what it imports is content-named under /assets.

import maplibregl from "maplibre-gl";
import stylesheet from "maplibre-gl/dist/maplibre-gl.css?url";

import { foreignOrigin, setSiteOrigin, siteURL } from "../map/contracts.ts";
import { bootInset, type Inset } from "../map/inset.ts";
import { liveFactAt, type LiveFact } from "../map/live-facts.ts";
import { archiveMetadata, installLiveLayer, liveLegend, type LiveLegend } from "../map/live-layer.ts";

export interface MountOptions {
    camera?: Partial<maplibregl.MapOptions>;
    /** The night map, Glassing Data's and the default; false draws the day map. */
    night?: boolean;
    /** The live closures and fire layer. On unless false. */
    live?: boolean;
}

export interface LiveEmbed {
    /** The marks, the credit, the day published and the orders line, or null with no copy. */
    legend(): LiveLegend | null;
    /** What a tap at a screen point on the closures and fire layer says, or null. */
    factAt(point: { x: number; y: number }): LiveFact | null;
}

export interface Mounted {
    map: maplibregl.Map;
    maplibregl: typeof maplibregl;
    attribution: string;
    live: Promise<LiveEmbed | null>;
    destroy(): void;
}

function linkStylesheet() {
    const href = siteURL(stylesheet);
    if (document.querySelector(`link[href="${href}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    document.head.prepend(link);
}


export async function mount(container: HTMLElement, options: MountOptions = {}): Promise<Mounted> {
    setSiteOrigin(foreignOrigin(import.meta.url, location.origin));
    linkStylesheet();
    const night = options.night ?? true;
    const inset: Inset = await bootInset(container, { dark: () => night, camera: options.camera });
    const layer = options.live === false ? Promise.resolve(null) : installLiveLayer(inset.map, {
        base: () => ({ id: "topo", ground: "vector" }),
        night: () => night,
        metadata: archiveMetadata(inset.protocol),
    }).catch((error) => {
        console.warn("the live layer is off", error);
        return null;
    });
    const live = layer.then(async (on): Promise<LiveEmbed | null> => {
        if (!on) return null;
        await on.ready;
        return {
            legend: () => {
                const build = on.build();
                return build ? liveLegend(on.style, build, on.credit(), night) : null;
            },
            factAt: (point) => (on.build() ? liveFactAt(inset.map, point, on.metadata()) : null),
        };
    });
    // For the dev checks (`smoke/data-live-shots.mjs`), as the map page's `__glassingMap`.
    if (import.meta.env.DEV) (window as never as Record<string, unknown>).__glassingDataMap = inset.map;
    return {
        map: inset.map,
        maplibregl,
        attribution: inset.attribution,
        live,
        destroy: () => {
            void layer.then((on) => on?.dispose());
            inset.destroy();
        },
    };
}
